// src/pages/calidad/LotesRecepcionReporte.jsx
// KPI 5.4 Lotes Aprobados por Calidad (F-CAL-09) — pestañas Resumen / Histórico /
// Por producto o proveedor / Historial de lotes.
//
// Regla del KPI: aprobados (LIBERADO, RECHAZADO PARCIAL, MUESTRA) ÷ lotes revisados.
// La frecuencia oficial es semanal (ficha); la vista mensual es para comparar.

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import * as XLSX from 'xlsx';
import { FiDownload, FiRefreshCw, FiInfo, FiChevronLeft, FiChevronRight, FiChevronDown, FiChevronUp, FiExternalLink } from 'react-icons/fi';
import api from '../../services/api';
import s from '../mantenimiento/estilos';

Chart.register(...registerables);

const BASE = '/api/calidad/lotes-recepcion';
const COLOR = { verde: '#3E7C5A', amarillo: '#B8862E', rojo: '#B23A48', morado: '#4A1E5C', lila: '#A98BB8', gris: '#8A7690', naranja: '#B2662A' };
const ESTATUS = {
  LIBERADO: { color: '#3E7C5A', fondo: '#F0F7F1' },
  'RECHAZADO PARCIAL': { color: '#B2662A', fondo: '#FFF6E9' },
  MUESTRA: { color: '#6A2C75', fondo: '#EFE9F3' },
  RECHAZADO: { color: '#B23A48', fondo: '#FDF3F1' },
};

// ─── Helpers ───
const num = (v) => (v === null || v === undefined || v === '' ? null : parseFloat(v));
const fmtPct = (v) => (num(v) === null ? '—' : `${num(v).toFixed(1)}%`);
const fmtNum = (v) => (num(v) === null ? '—' : num(v).toLocaleString('es-MX', { maximumFractionDigits: 2 }));
const fmtFecha = (d) => (d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const nombreMes = (d) => new Date(d).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
const etiquetaSemana = (d) => `Sem. ${new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })}`;
const pad = (n) => String(n).padStart(2, '0');
const aISO = (d) => { const x = new Date(d); return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`; };

function rangoDeMes(mes) {
  const d = new Date(mes);
  const y = d.getFullYear();
  const m = d.getMonth();
  return { desde: `${y}-${pad(m + 1)}-01`, hasta: `${y}-${pad(m + 1)}-${pad(new Date(y, m + 1, 0).getDate())}` };
}
function mesAnterior(mes) {
  const d = new Date(mes);
  return new Date(d.getFullYear(), d.getMonth() - 1, 1);
}
function semaforo(v, meta) {
  if (num(v) === null) return 'gris';
  if (num(v) >= meta) return 'verde';
  if (num(v) >= meta - 10) return 'amarillo';
  return 'rojo';
}

function Grafica({ config, alto = 260 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return undefined;
    const chart = new Chart(ref.current, config);
    return () => chart.destroy();
  }, [config]);
  return <div style={{ position: 'relative', height: alto }}><canvas ref={ref} /></div>;
}

const TagEstatus = ({ estatus }) => {
  const e = ESTATUS[estatus] || { color: COLOR.gris, fondo: '#F0E9E0' };
  return <span style={{ ...x.tag, color: e.color, background: e.fondo }}>{estatus}</span>;
};

function descargarExcel(filas, hoja, archivo) {
  const ws = XLSX.utils.json_to_sheet(filas);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, hoja);
  XLSX.writeFile(wb, `${archivo}_${aISO(new Date())}.xlsx`);
}

function filasExcelLotes(lotes) {
  return lotes.map((l) => ({
    'Fecha recepción': fmtFecha(l.fecha_recepcion),
    'Muestra retención': l.muestra_retencion || '',
    Producto: l.producto,
    Proveedor: l.proveedor || '',
    Lote: l.lote || '',
    Caducidad: l.caducidad ? fmtFecha(l.caducidad) : '',
    'Cant. recibida': num(l.cantidad_recibida),
    'Cant. liberada': num(l.cantidad_liberada),
    'Cant. rechazada': num(l.cantidad_rechazada),
    Unidad: l.unidad || '',
    'Presentación': l.presentacion || '',
    Olor: l.olor || '',
    Color: l.color || '',
    Sabor: l.sabor || '',
    Apariencia: l.apariencia || '',
    Observaciones: l.observaciones || '',
    Estatus: l.estatus,
    'Recibido NetSuite': l.recibido_netsuite || '',
    OC: l.oc || '',
    'F-Alm': l.f_alm || '',
    'Link F-Alm': l.f_alm_link || '',
    'Folio de factura': l.factura || '',
    'Link certificado': l.certificado_link || '',
    'Capturado por': l.capturado_por || '',
  }));
}

export default function LotesRecepcionReporte() {
  const [tab, setTab] = useState('resumen');
  const [semanal, setSemanal] = useState([]);
  const [mensual, setMensual] = useState([]);
  const [meta, setMeta] = useState(90);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [mensaje, setMensaje] = useState(null);
  const [periodoHist, setPeriodoHist] = useState('semana');

  // Por producto / proveedor
  const [agruparPor, setAgruparPor] = useState('producto');
  const [mesAgrupado, setMesAgrupado] = useState(null);
  const [agrupado, setAgrupado] = useState({ actual: [], anterior: [] });
  const [cargandoAgrupado, setCargandoAgrupado] = useState(false);
  const [rechazosMes, setRechazosMes] = useState([]);

  // Historial
  const filtrosVacios = { desde: '', hasta: '', proveedor: '', producto: '', estatus: '', busqueda: '' };
  const [filtros, setFiltros] = useState(filtrosVacios);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(50);
  const [historial, setHistorial] = useState({ data: [], total: 0, totalPaginas: 1 });
  const [cargandoHist, setCargandoHist] = useState(false);
  const [abierto, setAbierto] = useState(null);
  const [proveedoresCatalogo, setProveedoresCatalogo] = useState([]);
  const [sincronizando, setSincronizando] = useState(false);
  const [exportando, setExportando] = useState(false);

  async function cargarAgrupado(por = agruparPor, mes = mesAgrupado) {
    if (!mes) return;
    try {
      setCargandoAgrupado(true);
      const [act, ant] = await Promise.all([
        api.get(`${BASE}/agrupado`, { params: { por, ...rangoDeMes(mes) } }),
        api.get(`${BASE}/agrupado`, { params: { por, ...rangoDeMes(mesAnterior(mes)) } }),
      ]);
      setAgrupado({ actual: act.data.data, anterior: ant.data.data });
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'No se pudo cargar el desglose.' });
    } finally {
      setCargandoAgrupado(false);
    }
  }

  async function cargarHistorial(f = filtros, p = pagina, pp = porPagina) {
    try {
      setCargandoHist(true);
      const params = { pagina: p, porPagina: pp };
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get(BASE, { params });
      setHistorial({ data: res.data.data, total: res.data.total, totalPaginas: res.data.totalPaginas });
      setAbierto(null);
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'No se pudo cargar el historial.' });
    } finally {
      setCargandoHist(false);
    }
  }

  useEffect(() => {
    (async () => {
      try {
        setCargando(true);
        const [sem, mes] = await Promise.all([
          api.get(`${BASE}/reporte`, { params: { periodo: 'semana', limite: 16 } }),
          api.get(`${BASE}/reporte`, { params: { periodo: 'mes', limite: 12 } }),
        ]);
        setSemanal(sem.data.data);
        setMensual(mes.data.data);
        if (sem.data.meta !== undefined && sem.data.meta !== null) setMeta(sem.data.meta);
        api.get('/api/catalogos/proveedores').then((r) => setProveedoresCatalogo(r.data.data)).catch(() => {});

        const mesesDesc = [...mes.data.data].reverse();
        const cerrado = mesesDesc.find((m) => !m.es_parcial) || mesesDesc[0];
        if (cerrado) {
          setMesAgrupado(cerrado.periodo);
          cargarAgrupado('producto', cerrado.periodo);
          api.get(`${BASE}/agrupado`, { params: { por: 'producto', ...rangoDeMes(cerrado.periodo) } })
            .then((r) => setRechazosMes(r.data.data.filter((p) => p.rechazados + p.rechazados_parcial > 0)))
            .catch(() => {});
        }
        await cargarHistorial(filtrosVacios, 1, porPagina);
        setError(null);
      } catch (err) {
        console.error(err);
        setError('No se pudo cargar el KPI de Lotes Aprobados por Calidad.');
      } finally {
        setCargando(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSincronizarCatalogos() {
    try {
      setSincronizando(true);
      await api.post('/api/catalogos/sync');
      const r = await api.get('/api/catalogos/proveedores');
      setProveedoresCatalogo(r.data.data);
      setMensaje({ tipo: 'ok', texto: 'Catálogos de productos y proveedores sincronizados con NetSuite.' });
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'No se pudieron sincronizar los catálogos.' });
    } finally {
      setSincronizando(false);
    }
  }

  function irAHistorial(extra) {
    const f = { ...filtrosVacios, ...extra };
    setFiltros(f);
    setPagina(1);
    setTab('historial');
    cargarHistorial(f, 1);
  }

  async function exportarHistorial(todo) {
    try {
      setExportando(true);
      const params = {};
      if (!todo) Object.entries(filtros).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get(BASE, { params });
      descargarExcel(filasExcelLotes(res.data.data), 'Lotes', todo ? 'Lotes_recepcion_completo' : 'Lotes_recepcion_filtrado');
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'No se pudo generar el Excel.' });
    } finally {
      setExportando(false);
    }
  }

  // ─── Derivados ───
  const semDesc = useMemo(() => [...semanal].reverse(), [semanal]);
  const mesDesc = useMemo(() => [...mensual].reverse(), [mensual]);
  const semCerrada = semDesc.find((r) => !r.es_parcial) || null;
  const semPrev = semCerrada ? semDesc[semDesc.indexOf(semCerrada) + 1] : null;
  const semEnCurso = semDesc.find((r) => r.es_parcial) || null;
  const mesCerrado = mesDesc.find((r) => !r.es_parcial) || null;
  const mesPrev = mesCerrado ? mesDesc[mesDesc.indexOf(mesCerrado) + 1] : null;

  const serieHist = periodoHist === 'mes' ? mensual : semanal;
  const etiquetaPeriodo = (r) => (periodoHist === 'mes'
    ? new Date(r.periodo).toLocaleDateString('es-MX', { month: 'short', year: '2-digit' })
    : new Date(r.periodo).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })) + (r.es_parcial ? '*' : '');

  const configPct = useMemo(() => ({
    type: 'line',
    data: {
      labels: serieHist.map(etiquetaPeriodo),
      datasets: [
        {
          label: '% lotes aprobados', data: serieHist.map((r) => num(r.porcentaje)), borderColor: COLOR.morado, backgroundColor: COLOR.morado,
          borderWidth: 2.5, pointRadius: 5, tension: 0.25,
          pointBackgroundColor: serieHist.map((r) => (r.es_parcial ? '#fff' : COLOR[semaforo(r.porcentaje, meta)])),
        },
        { label: `Meta (${meta}%)`, data: serieHist.map(() => meta), borderColor: COLOR.verde, borderDash: [6, 4], borderWidth: 1.5, pointRadius: 0 },
      ],
    },
    options: {
      maintainAspectRatio: false,
      scales: { y: { suggestedMin: 50, max: 100, ticks: { callback: (v) => `${v}%` } } },
      plugins: { legend: { position: 'bottom' }, tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${fmtPct(c.raw)}` } } },
    },
  }), [serieHist, meta]); // eslint-disable-line react-hooks/exhaustive-deps

  const configEstatus = useMemo(() => ({
    type: 'bar',
    data: {
      labels: serieHist.map(etiquetaPeriodo),
      datasets: [
        { label: 'Liberado', data: serieHist.map((r) => r.liberados), backgroundColor: ESTATUS.LIBERADO.color },
        { label: 'Muestra', data: serieHist.map((r) => r.muestras), backgroundColor: COLOR.lila },
        { label: 'Rechazado parcial', data: serieHist.map((r) => r.rechazados_parcial), backgroundColor: COLOR.naranja },
        { label: 'Rechazado', data: serieHist.map((r) => r.rechazados), backgroundColor: COLOR.rojo },
      ],
    },
    options: {
      maintainAspectRatio: false,
      scales: { x: { stacked: true }, y: { stacked: true, title: { display: true, text: 'Lotes' } } },
      plugins: { legend: { position: 'bottom' } },
    },
  }), [serieHist]); // eslint-disable-line react-hooks/exhaustive-deps

  const filasAgrupado = useMemo(() => {
    const prev = Object.fromEntries(agrupado.anterior.map((r) => [r.nombre, r]));
    return agrupado.actual.map((r) => ({ ...r, anterior: prev[r.nombre] || null }));
  }, [agrupado]);

  const configAgrupado = useMemo(() => {
    const top = [...filasAgrupado].sort((a, b) => b.total_revisados - a.total_revisados).slice(0, 12);
    return {
      type: 'bar',
      data: {
        labels: top.map((r) => (r.nombre.length > 28 ? `${r.nombre.slice(0, 27)}…` : r.nombre)),
        datasets: [
          { label: 'Mes seleccionado', data: top.map((r) => num(r.porcentaje)), backgroundColor: top.map((r) => COLOR[semaforo(r.porcentaje, meta)]) },
          { label: 'Mes anterior', data: top.map((r) => num(r.anterior?.porcentaje)), backgroundColor: '#D9CDE0' },
        ],
      },
      options: {
        indexAxis: 'y',
        maintainAspectRatio: false,
        scales: { x: { min: 0, max: 100, ticks: { callback: (v) => `${v}%` } } },
        plugins: { legend: { position: 'bottom' }, tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${fmtPct(c.raw)}` } } },
      },
    };
  }, [filasAgrupado, meta]);

  if (cargando) return <div style={s.paginaContenedor}>Cargando...</div>;
  if (error) return <div style={{ ...s.paginaContenedor, color: COLOR.rojo }}>{error}</div>;

  const valorMesFiltro = mensual.some((r) => { const g = rangoDeMes(r.periodo); return g.desde === filtros.desde && g.hasta === filtros.hasta; }) ? filtros.desde : '';

  return (
    <div style={s.paginaContenedor} className="contenedor-captura">
      <div style={{ marginBottom: 14 }}>
        <h1 style={{ ...s.tituloPagina, marginBottom: 2 }}>5.4 Lotes Aprobados por Calidad</h1>
        <div style={{ fontSize: 12, color: COLOR.gris }}>F-CAL-09 · Lista de recepción de materiales</div>
      </div>

      {mensaje && (
        <div style={{ ...s.mensaje, color: mensaje.tipo === 'ok' ? COLOR.verde : COLOR.rojo, display: 'flex', justifyContent: 'space-between' }}>
          {mensaje.texto}
          <button style={s.linkChico} onClick={() => setMensaje(null)}>cerrar</button>
        </div>
      )}

      <div style={s.tabBar}>
        {[['resumen', 'Resumen'], ['historico', 'Histórico'], ['agrupado', 'Por producto / proveedor'], ['historial', 'Historial de lotes']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{ ...s.tabBtn, ...(tab === k ? s.tabBtnActivo : {}) }}>{label}</button>
        ))}
      </div>

      {/* ═══════════ RESUMEN ═══════════ */}
      {tab === 'resumen' && (
        <>
          <div style={s.kgrid}>
            <TarjetaPct titulo={semCerrada ? `Semana cerrada · ${etiquetaSemana(semCerrada.periodo)}` : 'Semana cerrada'} fila={semCerrada} previo={semPrev} meta={meta} principal etiquetaPrevio="semana anterior" />
            <TarjetaPct titulo={mesCerrado ? `Mes cerrado · ${nombreMes(mesCerrado.periodo)}` : 'Mes cerrado'} fila={mesCerrado} previo={mesPrev} meta={meta} etiquetaPrevio="mes anterior" />
            <div style={{ ...s.kcard, borderTop: `3px dashed ${COLOR.gris}` }}>
              <div style={s.kLbl}>Semana en curso {semEnCurso && `· ${etiquetaSemana(semEnCurso.periodo)}`}</div>
              <div style={{ ...s.kVal, fontSize: 30, color: '#2A1830' }}>{semEnCurso ? fmtPct(semEnCurso.porcentaje) : '—'}</div>
              <div style={s.kSub}>{semEnCurso ? `${semEnCurso.total_aprobados} de ${semEnCurso.total_revisados} lotes hasta hoy (parcial)` : 'Sin lotes esta semana todavía.'}</div>
            </div>
            <div style={{ ...s.kcard, borderTop: `3px solid ${COLOR.rojo}`, cursor: mesCerrado ? 'pointer' : 'default' }}
              onClick={() => mesCerrado && irAHistorial({ ...rangoDeMes(mesCerrado.periodo), estatus: 'CON_RECHAZO' })}>
              <div style={s.kLbl}>Lotes con rechazo {mesCerrado && `· ${nombreMes(mesCerrado.periodo)}`}</div>
              <div style={{ ...s.kVal, fontSize: 30, color: COLOR.rojo }}>{mesCerrado ? mesCerrado.rechazados + mesCerrado.rechazados_parcial : '—'}</div>
              <div style={s.kSub}>{mesCerrado ? `${mesCerrado.rechazados} rechazados · ${mesCerrado.rechazados_parcial} rechazo parcial. Clic para verlos.` : ''}</div>
            </div>
          </div>

          {rechazosMes.length > 0 && (
            <div style={s.tarjeta}>
              <div style={s.seccionTitulo}>Productos con rechazo en {nombreMes(mesCerrado.periodo)}</div>
              <div style={s.tablaScroll}>
                <table style={s.tabla}>
                  <thead>
                    <tr>
                      <th style={s.th}>Producto</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>Lotes</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>Rechazados</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>Rechazo parcial</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>Cantidad rechazada</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rechazosMes.slice(0, 8).map((p) => (
                      <tr key={p.nombre} style={{ cursor: 'pointer' }} onClick={() => irAHistorial({ ...rangoDeMes(mesCerrado.periodo), producto: p.nombre })}>
                        <td style={{ ...s.td, whiteSpace: 'normal' }}>{p.nombre}</td>
                        <td style={{ ...s.td, textAlign: 'right' }}>{p.total_revisados}</td>
                        <td style={{ ...s.td, textAlign: 'right', color: p.rechazados ? COLOR.rojo : undefined }}>{p.rechazados}</td>
                        <td style={{ ...s.td, textAlign: 'right', color: p.rechazados_parcial ? COLOR.naranja : undefined }}>{p.rechazados_parcial}</td>
                        <td style={{ ...s.td, textAlign: 'right' }}>{fmtNum(p.cantidad_rechazada)} {p.unidades_distintas > 1 ? '(varias unidades)' : p.unidad || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div style={s.seccionKpiBloque}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <FiInfo style={{ color: COLOR.morado, marginTop: 2, flexShrink: 0 }} />
              <div style={s.seccionKpiFormula}>
                <strong style={{ color: '#2A1830' }}>Cómo se calcula.</strong> Lotes aprobados ÷ lotes revisados. Cuentan como aprobados los lotes
                LIBERADOS, con RECHAZO PARCIAL y de MUESTRA; solo el RECHAZADO total no se aprueba. La medición oficial es semanal; el mes sirve para comparar tendencias.
                Meta: {meta}% o más.
                <br />
                <strong style={{ color: '#2A1830' }}>De dónde sale.</strong> Captura de Recepción de Materiales (F-CAL-09) en la plataforma.
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══════════ HISTÓRICO ═══════════ */}
      {tab === 'historico' && (
        <>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            {[['semana', 'Por semana'], ['mes', 'Por mes']].map(([k, label]) => (
              <button key={k} onClick={() => setPeriodoHist(k)} style={{ ...s.subTabBtn, ...(periodoHist === k ? s.subTabBtnActivo : {}) }}>{label}</button>
            ))}
          </div>
          <div style={s.tarjeta}>
            <div style={s.seccionTitulo}>% de lotes aprobados</div>
            <Grafica config={configPct} />
            <div style={{ ...s.kSub, marginTop: 8 }}>* Periodo en curso, parcial.</div>
          </div>
          <div style={s.tarjeta}>
            <div style={s.seccionTitulo}>Lotes por estatus</div>
            <Grafica config={configEstatus} alto={240} />
          </div>

          <div style={{ ...s.headerFila, margin: '6px 0 8px' }}>
            <h3 style={s.subtituloLista}>Tabla {periodoHist === 'mes' ? 'mensual' : 'semanal'}</h3>
            <button
              onClick={() => descargarExcel([...serieHist].reverse().map((r) => ({
                Periodo: periodoHist === 'mes' ? nombreMes(r.periodo) : etiquetaSemana(r.periodo),
                '% aprobados': num(r.porcentaje), Meta: meta, Revisados: r.total_revisados, Aprobados: r.total_aprobados,
                Liberados: r.liberados, Muestra: r.muestras, 'Rechazo parcial': r.rechazados_parcial, Rechazados: r.rechazados,
              })), 'Histórico', `Lotes_aprobados_${periodoHist}`)}
              style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <FiDownload /> Descargar Excel
            </button>
          </div>
          <div style={s.tablaScroll}>
            <table style={s.tabla}>
              <thead>
                <tr>
                  <th style={s.th}>{periodoHist === 'mes' ? 'Mes' : 'Semana'}</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>% aprobados</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Revisados</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Liberados</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Muestra</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Rechazo parcial</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Rechazados</th>
                </tr>
              </thead>
              <tbody>
                {[...serieHist].reverse().map((r) => (
                  <tr key={r.periodo} style={{ cursor: 'pointer' }}
                    onClick={() => {
                      const desde = aISO(r.periodo);
                      const fin = periodoHist === 'mes' ? rangoDeMes(r.periodo).hasta : aISO(new Date(new Date(r.periodo).getTime() + 6 * 86400000));
                      irAHistorial({ desde, hasta: fin });
                    }}>
                    <td style={s.td}>{periodoHist === 'mes' ? nombreMes(r.periodo) : etiquetaSemana(r.periodo)}{r.es_parcial && <span style={{ ...x.tag, color: '#7a5417', background: '#F9F0DD', marginLeft: 6 }}>parcial</span>}</td>
                    <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, color: r.es_parcial ? COLOR.gris : COLOR[semaforo(r.porcentaje, meta)] }}>{fmtPct(r.porcentaje)}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{r.total_revisados}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{r.liberados}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{r.muestras}</td>
                    <td style={{ ...s.td, textAlign: 'right', color: r.rechazados_parcial ? COLOR.naranja : undefined }}>{r.rechazados_parcial}</td>
                    <td style={{ ...s.td, textAlign: 'right', color: r.rechazados ? COLOR.rojo : undefined }}>{r.rechazados}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ═══════════ POR PRODUCTO / PROVEEDOR ═══════════ */}
      {tab === 'agrupado' && (
        <>
          <div style={{ ...s.filtrosBar, alignItems: 'center' }}>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Ver por</span>
              <div style={{ display: 'flex', gap: 6 }}>
                {[['producto', 'Producto'], ['proveedor', 'Proveedor']].map(([k, label]) => (
                  <button key={k} onClick={() => { setAgruparPor(k); cargarAgrupado(k, mesAgrupado); }} style={{ ...s.subTabBtn, ...(agruparPor === k ? s.subTabBtnActivo : {}) }}>{label}</button>
                ))}
              </div>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Mes</span>
              <select style={s.filtroInput} value={mesAgrupado ? aISO(mesAgrupado) : ''}
                onChange={(e) => { const m = mensual.find((r) => aISO(r.periodo) === e.target.value)?.periodo; setMesAgrupado(m); cargarAgrupado(agruparPor, m); }}>
                {mesDesc.map((r) => <option key={r.periodo} value={aISO(r.periodo)}>{nombreMes(r.periodo)}{r.es_parcial ? ' (parcial)' : ''}</option>)}
              </select>
            </div>
            <button
              disabled={filasAgrupado.length === 0}
              onClick={() => descargarExcel(filasAgrupado.map((r) => ({
                [agruparPor === 'producto' ? 'Producto' : 'Proveedor']: r.nombre,
                '% aprobados': num(r.porcentaje), '% mes anterior': num(r.anterior?.porcentaje),
                Revisados: r.total_revisados, Liberados: r.liberados, Muestra: r.muestras,
                'Rechazo parcial': r.rechazados_parcial, Rechazados: r.rechazados,
                'Cant. recibida': num(r.cantidad_recibida), 'Cant. rechazada': num(r.cantidad_rechazada),
                Unidad: r.unidades_distintas > 1 ? 'varias' : r.unidad || '',
              })), 'Desglose', `Lotes_por_${agruparPor}_${mesAgrupado ? aISO(mesAgrupado).slice(0, 7) : ''}`)}
              style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}
            >
              <FiDownload /> Descargar Excel
            </button>
          </div>

          {cargandoAgrupado && <div style={s.subtitulo}>Cargando...</div>}
          {!cargandoAgrupado && filasAgrupado.length === 0 && <div style={s.subtitulo}>Sin lotes en este mes.</div>}
          {!cargandoAgrupado && filasAgrupado.length > 0 && (
            <>
              <div style={s.tarjeta}>
                <div style={s.seccionTitulo}>% aprobados por {agruparPor} contra el mes anterior (los 12 con más lotes)</div>
                <Grafica config={configAgrupado} alto={Math.max(220, Math.min(12, filasAgrupado.length) * 34 + 60)} />
              </div>
              <div style={s.subtitulo}>Del que peor resultado tuvo al mejor. Haz clic en una fila para ver sus lotes.</div>
              <div style={s.tablaScroll}>
                <table style={s.tabla}>
                  <thead>
                    <tr>
                      <th style={s.th}>{agruparPor === 'producto' ? 'Producto' : 'Proveedor'}</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>% aprobados</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>vs mes anterior</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>Lotes</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>Rechazo parcial</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>Rechazados</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>Recibido</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>Rechazado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filasAgrupado.map((r) => {
                      const prev = num(r.anterior?.porcentaje);
                      const d = prev !== null && num(r.porcentaje) !== null ? num(r.porcentaje) - prev : null;
                      const unidad = r.unidades_distintas > 1 ? '' : r.unidad || '';
                      return (
                        <tr key={r.nombre} style={{ cursor: 'pointer' }} onClick={() => irAHistorial({ ...rangoDeMes(mesAgrupado), [agruparPor]: r.nombre })}>
                          <td style={{ ...s.td, whiteSpace: 'normal', minWidth: 200 }}>{r.nombre}</td>
                          <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, color: COLOR[semaforo(r.porcentaje, meta)] }}>{fmtPct(r.porcentaje)}</td>
                          <td style={{ ...s.td, textAlign: 'right', color: d === null || d === 0 ? COLOR.gris : d > 0 ? COLOR.verde : COLOR.rojo }}>
                            {d === null ? (r.anterior ? '—' : 'sin lotes') : `${d > 0 ? '▲' : d < 0 ? '▼' : '='} ${Math.abs(d).toFixed(1)} pts`}
                          </td>
                          <td style={{ ...s.td, textAlign: 'right' }}>{r.total_revisados}</td>
                          <td style={{ ...s.td, textAlign: 'right', color: r.rechazados_parcial ? COLOR.naranja : undefined }}>{r.rechazados_parcial}</td>
                          <td style={{ ...s.td, textAlign: 'right', color: r.rechazados ? COLOR.rojo : undefined }}>{r.rechazados}</td>
                          <td style={{ ...s.td, textAlign: 'right' }}>{fmtNum(r.cantidad_recibida)} {unidad}</td>
                          <td style={{ ...s.td, textAlign: 'right' }}>{num(r.cantidad_rechazada) ? `${fmtNum(r.cantidad_rechazada)} ${unidad}` : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {/* ═══════════ HISTORIAL ═══════════ */}
      {tab === 'historial' && (
        <>
          <div style={s.filtrosBar}>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Mes</span>
              <select style={s.filtroInput} value={valorMesFiltro}
                onChange={(e) => setFiltros(e.target.value ? { ...filtros, ...rangoDeMes(e.target.value + 'T00:00:00') } : { ...filtros, desde: '', hasta: '' })}>
                <option value="">Todo / rango personalizado</option>
                {mesDesc.map((r) => <option key={r.periodo} value={aISO(r.periodo)}>{nombreMes(r.periodo)}</option>)}
              </select>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Desde</span>
              <input type="date" style={s.filtroInput} value={filtros.desde} onChange={(e) => setFiltros({ ...filtros, desde: e.target.value })} />
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Hasta</span>
              <input type="date" style={s.filtroInput} value={filtros.hasta} onChange={(e) => setFiltros({ ...filtros, hasta: e.target.value })} />
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Proveedor</span>
              <select style={{ ...s.filtroInput, maxWidth: 220 }} value={filtros.proveedor} onChange={(e) => setFiltros({ ...filtros, proveedor: e.target.value })}>
                <option value="">Todos</option>
                {proveedoresCatalogo.map((p) => <option key={p.netsuite_id} value={p.nombre}>{p.nombre}</option>)}
              </select>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Estatus</span>
              <select style={s.filtroInput} value={filtros.estatus} onChange={(e) => setFiltros({ ...filtros, estatus: e.target.value })}>
                <option value="">Todos</option>
                <option value="CON_RECHAZO">Con rechazo (total o parcial)</option>
                {Object.keys(ESTATUS).map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Buscar</span>
              <input type="text" placeholder="Producto, lote, OC, folio, presentación…" style={{ ...s.filtroInput, minWidth: 210 }}
                value={filtros.busqueda} onChange={(e) => setFiltros({ ...filtros, busqueda: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') { setPagina(1); cargarHistorial(filtros, 1); } }} />
            </div>
            <button style={s.botonBuscar2} onClick={() => { setPagina(1); cargarHistorial(filtros, 1); }}>Buscar</button>
            <button style={s.linkChico} onClick={() => { setFiltros(filtrosVacios); setPagina(1); cargarHistorial(filtrosVacios, 1); }}>Limpiar</button>
          </div>

          {filtros.producto && (
            <div style={{ ...s.avisoAdvertencia, marginTop: 0, marginBottom: 10, display: 'flex', gap: 10, alignItems: 'center' }}>
              Producto: <strong>{filtros.producto}</strong>
              <button style={s.linkChico} onClick={() => { const f = { ...filtros, producto: '' }; setFiltros(f); setPagina(1); cargarHistorial(f, 1); }}>quitar</button>
            </div>
          )}

          <div style={{ ...s.headerFila, marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontSize: 12, color: COLOR.gris }}>{cargandoHist ? 'Cargando...' : `${historial.total.toLocaleString('es-MX')} lotes`}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleSincronizarCatalogos} disabled={sincronizando} style={{ ...x.botonSecundario, display: 'flex', alignItems: 'center', gap: 6 }}>
                <FiRefreshCw /> {sincronizando ? 'Sincronizando...' : 'Sincronizar catálogos'}
              </button>
              <button onClick={() => exportarHistorial(false)} disabled={exportando || historial.total === 0} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}>
                <FiDownload /> Exportar filtrado
              </button>
              <button onClick={() => exportarHistorial(true)} disabled={exportando} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}>
                <FiDownload /> {exportando ? 'Exportando...' : 'Exportar todo'}
              </button>
            </div>
          </div>

          <div style={s.tablaScroll}>
            <table style={s.tabla}>
              <thead>
                <tr>
                  <th style={s.th}></th>
                  <th style={s.th}>Fecha</th>
                  <th style={s.th}>Producto / lote</th>
                  <th style={s.th}>Proveedor</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Recibido</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Rechazado</th>
                  <th style={s.th}>Caducidad</th>
                  <th style={s.th}>OC / Folio factura</th>
                  <th style={s.th}>Estatus</th>
                </tr>
              </thead>
              <tbody>
                {!cargandoHist && historial.data.length === 0 && (
                  <tr><td colSpan={9} style={{ ...s.td, color: COLOR.gris, padding: 16 }}>Sin lotes para este filtro.</td></tr>
                )}
                {historial.data.map((l) => {
                  const esAbierto = abierto === l.id;
                  return (
                    <Fragment key={l.id}>
                      <tr onClick={() => setAbierto(esAbierto ? null : l.id)} style={{ cursor: 'pointer' }}>
                        <td style={s.td}>{esAbierto ? <FiChevronUp /> : <FiChevronDown />}</td>
                        <td style={s.td}>{fmtFecha(l.fecha_recepcion)}</td>
                        <td style={{ ...s.td, whiteSpace: 'normal', minWidth: 200 }}>
                          <strong>{l.producto}</strong>
                          {l.lote && <div style={x.sub}>Lote {l.lote}</div>}
                          {l.observaciones && <div style={{ ...x.sub, fontStyle: 'italic', color: '#3A2E42' }}>{l.observaciones}</div>}
                        </td>
                        <td style={{ ...s.td, whiteSpace: 'normal' }}>{l.proveedor || '—'}</td>
                        <td style={{ ...s.td, textAlign: 'right' }}>
                          {fmtNum(l.cantidad_recibida)} {l.unidad || ''}
                          {l.presentacion && <div style={x.sub}>{l.presentacion}</div>}
                        </td>
                        <td style={{ ...s.td, textAlign: 'right', color: num(l.cantidad_rechazada) ? COLOR.rojo : undefined }}>{num(l.cantidad_rechazada) ? `${fmtNum(l.cantidad_rechazada)} ${l.unidad || ''}` : '—'}</td>
                        <td style={s.td}>{l.caducidad ? fmtFecha(l.caducidad) : '—'}</td>
                        <td style={s.td}>
                          {l.oc ? `OC ${l.oc}` : '—'}
                          {l.factura && <div style={x.sub}>Folio {l.factura}</div>}
                        </td>
                        <td style={s.td}><TagEstatus estatus={l.estatus} /></td>
                      </tr>
                      {esAbierto && (
                        <tr>
                          <td colSpan={9} style={x.celdaDetalle}>
                            <div style={x.detalleGrid}>
                              <Dato label="Olor" valor={l.olor} />
                              <Dato label="Color" valor={l.color} />
                              <Dato label="Sabor" valor={l.sabor} />
                              <Dato label="Apariencia" valor={l.apariencia} />
                              <Dato label="Cantidad liberada" valor={num(l.cantidad_liberada) !== null ? `${fmtNum(l.cantidad_liberada)} ${l.unidad || ''}` : null} />
                              <Dato label="Presentación" valor={l.presentacion} />
                              <Dato label="Muestra de retención" valor={l.muestra_retencion} />
                              <Dato label="Recibido en NetSuite" valor={l.recibido_netsuite} />
                              <Dato label="F-Alm" valor={l.f_alm} />
                              <Dato label="Capturado por" valor={l.capturado_por} />
                            </div>
                            <div style={{ display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap' }}>
                              {l.certificado_link && <a href={l.certificado_link} target="_blank" rel="noreferrer" style={x.link}><FiExternalLink /> Certificado</a>}
                              {l.f_alm_link && <a href={l.f_alm_link} target="_blank" rel="noreferrer" style={x.link}><FiExternalLink /> Documento F-Alm</a>}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={x.paginacion}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={s.filtroLabel}>Por página</span>
              <select style={s.inputChico} value={porPagina} onChange={(e) => { const pp = Number(e.target.value); setPorPagina(pp); setPagina(1); cargarHistorial(filtros, 1, pp); }}>
                {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button style={x.botonPagina} disabled={pagina <= 1 || cargandoHist} onClick={() => { const p = pagina - 1; setPagina(p); cargarHistorial(filtros, p); }} aria-label="Página anterior"><FiChevronLeft /></button>
              <span style={{ fontSize: 12 }}>Página {pagina} de {historial.totalPaginas}</span>
              <button style={x.botonPagina} disabled={pagina >= historial.totalPaginas || cargandoHist} onClick={() => { const p = pagina + 1; setPagina(p); cargarHistorial(filtros, p); }} aria-label="Página siguiente"><FiChevronRight /></button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function TarjetaPct({ titulo, fila, previo, meta, principal, etiquetaPrevio }) {
  const v = fila ? num(fila.porcentaje) : null;
  const sem = semaforo(v, meta);
  const color = principal ? COLOR[sem] : COLOR.morado;
  const p = previo ? num(previo.porcentaje) : null;
  const d = v !== null && p !== null ? v - p : null;
  return (
    <div style={{ ...s.kcard, borderTop: `3px solid ${color}` }}>
      <div style={s.kLbl}>{titulo}</div>
      <div style={{ ...s.kVal, fontSize: 32, color }}>{fmtPct(v)}</div>
      {principal && v !== null && (
        <div style={{ fontSize: 12.5, fontWeight: 600, margin: '6px 0', color }}>
          {v >= meta ? `Cumple la meta de ${meta}%` : `No cumple la meta de ${meta}% (${(v - meta).toFixed(1)} pts)`}
        </div>
      )}
      <div style={s.kSub}>{fila ? `${fila.total_aprobados} de ${fila.total_revisados} lotes aprobados` : 'Sin datos'}</div>
      {d !== null && (
        <div style={{ ...s.kSub, color: d > 0 ? COLOR.verde : d < 0 ? COLOR.rojo : COLOR.gris }}>
          {d > 0 ? '▲' : d < 0 ? '▼' : '='} {Math.abs(d).toFixed(1)} pts vs {etiquetaPrevio} ({fmtPct(p)})
        </div>
      )}
    </div>
  );
}

const Dato = ({ label, valor }) => (
  <div>
    <div style={{ fontSize: 10, color: '#8A7690', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
    <div style={{ fontSize: 12.5, color: '#2A1830' }}>{valor || '—'}</div>
  </div>
);

const x = {
  tag: { display: 'inline-flex', alignItems: 'center', fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap' },
  sub: { fontSize: 10.5, color: '#8A7690' },
  celdaDetalle: { padding: '14px 18px 16px 44px', background: '#FAF6F0', borderBottom: '1px solid #F0E9E0' },
  detalleGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 },
  link: { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#4A1E5C', fontWeight: 600 },
  paginacion: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 12 },
  botonPagina: { width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #E6DDD2', background: '#fff', borderRadius: 6, cursor: 'pointer', color: '#4A1E5C' },
  botonSecundario: { padding: '6px 14px', fontSize: 12, fontWeight: 600, color: '#4A1E5C', background: '#EFE9F3', border: '1px solid #A98BB8', borderRadius: 6, cursor: 'pointer' },
};